/**
 * WebhooksSettings Component
 *
 * Webhooks settings tab content.
 * Configure global webhook for system event notifications.
 */
import { Badge, Button, Divider, Icon, Input, InputWrapper, URLInput } from '@insio/ui-toolkit';
import { useWebhooksSettings } from '../../hooks/use-webhooks-settings';
import { ClearInputButton } from './ClearInputButton';

export function WebhooksSettings() {
  const {
    t,
    config,
    loading,
    isSaving,
    isTogglingActive,
    urlError,
    headerValueError,
    tokenFromServer,
    canSave,
    activationLocked,
    handleUrlChange,
    handleClearUrl,
    handleBearerTokenChange,
    handleClearBearerToken,
    handleBearerTokenNameChange,
    handleClearBearerTokenName,
    handleToggleActive,
    handleSave
  } = useWebhooksSettings();

  return (
    <div className='space-y-lg'>
      {/* Status row: label + badge */}
      <div className='flex items-center gap-md'>
        <span className='text-sm font-medium text-foreground'>{t('status.label')}</span>
        {config.isActive ? (
          <Badge color='success' size='sm'>
            {t('statusBadge.active')}
          </Badge>
        ) : (
          <Badge color='error' size='sm'>
            {t('statusBadge.inactive')}
          </Badge>
        )}
      </div>

      {/* URL Address Input */}
      <InputWrapper
        label={t('fields.url')}
        htmlFor='webhooks-url'
        labelSize='sm'
        required={config.isActive}
      >
        <URLInput
          id='webhooks-url'
          value={config.url}
          onChange={handleUrlChange}
          placeholder={t('placeholders.url')}
          autoComplete='off'
          rightContent={
            config.url ? (
              <ClearInputButton
                onClick={handleClearUrl}
                testId='webhooks-clear-url'
                ariaLabel={t('actions.clearUrl')}
              />
            ) : undefined
          }
          hasError={!!urlError}
        />
        {urlError && <p className='text-sm text-error-text mt-xs'>{urlError}</p>}
        {!urlError && <p className='text-sm text-foreground-muted mt-xs'>{t('hints.url')}</p>}
      </InputWrapper>

      {/* Separator */}
      <Divider
        orientation='horizontal'
        size='full'
        variant='light'
        thickness='sm'
        className='mt-xl mb-xl'
      />

      {/* Authentication section */}
      <div className='space-y-md'>
        <div className='flex items-center gap-sm'>
          <Icon name='lock' size='sm' className='text-foreground-muted' />
          <h3 className='font-semibold text-foreground'>{t('fields.authTitle')}</h3>
        </div>

        <InputWrapper
          label={t('fields.headerName')}
          htmlFor='webhooks-bearer-token-name'
          labelSize='sm'
        >
          <Input
            id='webhooks-bearer-token-name'
            value={config.headerName}
            onChange={handleBearerTokenNameChange}
            placeholder={t('placeholders.headerName')}
            type='text'
            autoComplete='off'
            leftContent={<Icon name='pen-nib' size='sm' />}
            rightContent={
              config.headerName ? (
                <ClearInputButton
                  onClick={handleClearBearerTokenName}
                  testId='webhooks-clear-bearer-token-name'
                  ariaLabel={t('actions.deleteHeader')}
                />
              ) : undefined
            }
          />
        </InputWrapper>

        <InputWrapper
          label={t('fields.headerValue')}
          htmlFor='webhooks-bearer-token'
          labelSize='sm'
        >
          <Input
            id='webhooks-bearer-token'
            value={config.headerValue}
            onChange={handleBearerTokenChange}
            placeholder={t('placeholders.headerValue')}
            type='password'
            disabled={tokenFromServer}
            readOnly={tokenFromServer}
            autoComplete='new-password'
            leftContent={<Icon name='lock' size='sm' />}
            rightContent={
              tokenFromServer || config.headerValue ? (
                <ClearInputButton
                  onClick={handleClearBearerToken}
                  testId='webhooks-clear-bearer-token'
                  ariaLabel={t('actions.deleteHeader')}
                />
              ) : undefined
            }
            hasError={!!headerValueError}
          />
          {headerValueError && <p className='text-sm text-error-text mt-xs'>{headerValueError}</p>}
        </InputWrapper>
      </div>

      <p className='text-sm text-foreground-muted mt-md mb-16'>{t('hints.authToken')}</p>

      {/* Separator */}
      <Divider orientation='horizontal' size='full' variant='light' thickness='sm' />

      {/* Action buttons */}
      <div className='flex justify-end gap-sm mt-xl'>
        <Button
          variant={config.isActive ? 'error' : 'success'}
          onClick={() => void handleToggleActive()}
          disabled={
            isTogglingActive || isSaving || loading || (!config.isActive && activationLocked)
          }
          testId='webhooks-toggle-active'
          className='mx-0'
        >
          {isTogglingActive
            ? t('actions.togglingActive')
            : config.isActive
              ? t('actions.deactivate')
              : t('actions.activate')}
        </Button>
        <Button
          variant='primary'
          onClick={() => void handleSave()}
          disabled={isSaving || loading || !canSave}
          testId='webhooks-save'
          className='mx-0'
        >
          {isSaving ? t('actions.saving') : t('actions.save')}
        </Button>
      </div>
    </div>
  );
}
